/**
 * Google Consent Mode v2. Defaults are denied until the visitor decides, so Google tags
 * (gtag.js, GTM) load but do not store cookies. Categories map to Google's consent types.
 */
type Gtag = (...args: unknown[]) => void;

interface DataLayerWindow {
  dataLayer?: unknown[];
  gtag?: Gtag;
}

function gtag(win: DataLayerWindow): Gtag {
  win.dataLayer = win.dataLayer || [];
  // gtag must push the arguments object itself, not an array copy.
  return function () {
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer!.push(arguments);
  };
}

export function consentModeState(
  allowed: (category: string) => boolean,
): Record<string, "granted" | "denied"> {
  const state = (granted: boolean) => (granted ? "granted" : "denied");
  return {
    ad_storage: state(allowed("marketing")),
    ad_user_data: state(allowed("marketing")),
    ad_personalization: state(allowed("marketing")),
    analytics_storage: state(allowed("statistics")),
    functionality_storage: state(allowed("preferences")),
    personalization_storage: state(allowed("preferences")),
    security_storage: "granted",
  };
}

export function setConsentDefaults(
  win: DataLayerWindow,
  allowed: (category: string) => boolean,
): void {
  gtag(win)("consent", "default", {
    ...consentModeState(allowed),
    wait_for_update: 500,
  });
  gtag(win)("set", "ads_data_redaction", !allowed("marketing"));
}

export interface ConsentModeOptions {
  /** Send consent updates to Google tags at all. */
  enabled: boolean;
  /** Pass ad click ids in links when ad storage is denied. */
  urlPassthrough: boolean;
  /** Redact ad click ids when ad storage is denied. */
  adsDataRedaction: boolean;
}

export const DEFAULT_CONSENT_MODE: ConsentModeOptions = {
  enabled: true,
  urlPassthrough: false,
  adsDataRedaction: true,
};

export function updateConsent(
  win: DataLayerWindow,
  allowed: (category: string) => boolean,
  categories: string[],
  options: ConsentModeOptions = DEFAULT_CONSENT_MODE,
): void {
  if (options.enabled) {
    gtag(win)("consent", "update", consentModeState(allowed));
    gtag(win)(
      "set",
      "ads_data_redaction",
      options.adsDataRedaction && !allowed("marketing"),
    );
    if (options.urlPassthrough) {
      gtag(win)("set", "url_passthrough", true);
    }
  }
  // For Google Tag Manager triggers.
  win.dataLayer = win.dataLayer || [];
  win.dataLayer.push({
    event: "berrycookie_consent_update",
    berrycookie: { categories },
  });
}
